
import { debounce } from "lodash";
import { defineComponent } from "vue";
import { Filter } from "@/types/filter.type";
import { Station } from "@/types/station.type";
import { SamplingPoint } from "@/types/samplingpoint.type";
import { ElRowClassNameParams } from "@/types/element.type";
import PageHeaderComponent from "../PageHeader.component.vue";
import { ElMessage } from "element-plus";
import { getStation } from "@/service/station.service";
import {
  postSamplingPoint,
  putSamplingPoint,
  getSamplingPoints,
} from "@/service/samplingpoint.service";
import { useStore } from "vuex";
import { RootState } from "@/types/state.type";

export default defineComponent({
  components: {
    PageHeaderComponent,
  },
  props: {
    canWrite: {
      type: Boolean,
      default: false,
    },
    containerId: {
      type: Number,
    },
    detailRouterName: {
      type: String,
    },
  },
  setup() {
    const store = useStore<RootState>();
    return { store };
  },
  data() {
    return {
      samplingPoints: [] as SamplingPoint[],
      stations: [] as Station[],
      currentPage: 0 as number,
      rowPerPage: 10 as number,
      totalRows: 0 as number,
      query: "" as string,
      warning: "",
    };
  },
  created() {
    // - apply delay
    this.search = debounce(this.refreshSamplingPoint, 300);
  },
  mounted() {
    this.currentPage = 1;
    this.refreshSamplingPoint();
    this.getStations();
  },
  methods: {
    search(): void {
      this.refreshSamplingPoint();
    },
    /**
     * table row css class name
     * @param params
     * @return css class name
     */
    tableRowClassName(params: ElRowClassNameParams<SamplingPoint>): string {
      return params.row && params.row.status ? "" : "disable-row";
    },

    /**
     * set page
     * @param page
     */
    changePaginationSize(size: number): void {
      this.rowPerPage = size;
      this.refreshSamplingPoint();
    },

    /**
     * change page
     * @param {number} page
     */
    changePaginationPage(page: number): void {
      this.currentPage = page;
      this.refreshSamplingPoint();
    },

    /**
     * refresh samplingPoint item from list items
     * @param item
     */
    async refreshSamplingPoint(item: SamplingPoint | undefined = undefined) {
      let index = -1;
      if (this.samplingPoints && item) {
        index = this.samplingPoints.findIndex((i) => i.id === item.id);
      }
      if (index !== -1 && item) {
        this.samplingPoints[index] = item;
      } else {
        const filter: Filter = {};
        if (this.query) {
          filter.multi = this.query;
        }
        if (this.containerId) {
          filter.fields = { "container.id": { operator: "equal", value: this.containerId } };
        }

        const pagination = await getSamplingPoints(this.currentPage, this.rowPerPage, filter);

        this.totalRows = pagination.totalRows;
        this.samplingPoints = pagination.data;
      }
    },

    /**
     * obtains the stations of a sampling point
     */
    async getStations() {
      this.stations = await getStation();
      this.stations.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    },

    /**
     * assigns te station selected to the sampling point
     */
    async saveChanges(item: SamplingPoint) {
      try {
        if (item.levelVariation) {
          item.levelVariation = Number(item.levelVariation.toFixed(2));
        }
        if (item.refDistanceWater) {
          item.refDistanceWater = Number(item.refDistanceWater.toFixed(2));
        }

        if (item.id === undefined) {
          await postSamplingPoint(item);
        } else {
          await putSamplingPoint(item);
        }
        ElMessage.success(this.$t("success.action"));
      } catch (warning) {
        this.warning = "warning.stationOccupied";
        ElMessage.warning(this.$t("warning.stationOccupied"));
        this.refreshSamplingPoint();
      }
    },

    async changeStation(item: SamplingPoint) {
      this.store.commit("samplingpoint/addSamplingPoint", item);
      this.$router.push(`/stations/samplingPoints/tablestation/${item.code}`);
    },
  },
});
